/**
 * The Nextmine journal.
 *
 * Posts are plain markdown kept in the repo, so they are server-side rendered
 * and readable by search engines and AI crawlers without any JS execution.
 *
 * Adding a post:
 *   1. Add an entry to posts.json (slug, title, description, dates, faq, related).
 *   2. Add src/blog/posts/<slug>.js exporting the markdown body as the default export.
 *   3. Import it in the bodies map below.
 *
 * posts.json is intentionally plain JSON so that the Express sitemap
 * (server/resources/sitemap.js) can require it too. Keep it CommonJS friendly.
 */
import postsMeta from './posts.json';

import howToTellIfGoldJewelryIsReal from './posts/how-to-tell-if-gold-jewelry-is-real';
import oldMineCutVsOldEuropeanCutDiamonds from './posts/old-mine-cut-vs-old-european-cut-diamonds';
import howToDateAntiqueJewelryByEra from './posts/how-to-date-antique-jewelry-by-era';
import howToSellAntiqueJewelryOnline from './posts/how-to-sell-antique-jewelry-online';
import howToPhotographJewelryWithYourPhone from './posts/how-to-photograph-jewelry-with-your-phone';
import artDecoVsArtNouveauJewelry from './posts/art-deco-vs-art-nouveau-jewelry';
import howToMeasureYourRingSizeAtHome from './posts/how-to-measure-your-ring-size-at-home';
import silverHallmarksExplained from './posts/silver-hallmarks-explained';
import howToTellIfAGemstoneIsReal from './posts/how-to-tell-if-a-gemstone-is-real';
import whereToSellPreLovedFineJewelryOnline from './posts/where-to-sell-pre-loved-fine-jewelry-online';
import howMuchIsMyJewelryWorth from './posts/how-much-is-my-jewelry-worth';
import howToSellJewelrySafelyOnline from './posts/how-to-sell-jewelry-safely-online';
import howToCleanAntiqueJewelry from './posts/how-to-clean-antique-jewelry';
import realVsFakePearls from './posts/real-vs-fake-pearls';
import necklaceLengthsAndChainTypes from './posts/necklace-lengths-and-chain-types';
import howToBuyAnAntiqueEngagementRing from './posts/how-to-buy-an-antique-engagement-ring';
import howToIdentifyAndDateACameo from './posts/how-to-identify-and-date-a-cameo';
import howToSpotFakeAntiqueJewelry from './posts/how-to-spot-fake-antique-jewelry';
import handmadeVsCastJewelry from './posts/handmade-vs-cast-jewelry';
import ringSettingsExplained from './posts/ring-settings-explained';
import antiqueEarringFittingsAndDating from './posts/antique-earring-fittings-and-dating';
import enamelJewelryExplained from './posts/enamel-jewelry-explained';
import antiqueCufflinksAndMensJewelry from './posts/antique-cufflinks-and-mens-jewelry';
import howToWriteAJewelryListingThatSells from './posts/how-to-write-a-jewelry-listing-that-sells';
import customsAndImportDutiesOnJewelry from './posts/customs-and-import-duties-on-jewelry';
import howToInsureAntiqueJewelry from './posts/how-to-insure-antique-jewelry';
import howToStartCollectingAntiqueJewelry from './posts/how-to-start-collecting-antique-jewelry';
import whatMakesAntiqueJewelryRare from './posts/what-makes-antique-jewelry-rare';

// Everything in the journal is published under Line's name, on the page and in
// the structured data. One constant so the byline and the schema never drift.
export const AUTHOR_NAME = 'Line Evelin Munkholm';
import howToPriceUsedJewelry from './posts/how-to-price-used-jewelry';
import howToPackAndShipJewelry from './posts/how-to-pack-and-ship-jewelry';
import howToDescribeJewelryCondition from './posts/how-to-describe-jewelry-condition';
import howToDocumentJewelryProvenance from './posts/how-to-document-jewelry-provenance';
import jewelryAppraisalsExplained from './posts/jewelry-appraisals-explained';
import whatIsEscrowAndWhyItProtectsBoth from './posts/what-is-escrow-and-why-it-protects-both';
import howToHandleOffersAndNegotiation from './posts/how-to-handle-offers-and-negotiation';
import shouldYouRestoreJewelryBeforeSelling from './posts/should-you-restore-jewelry-before-selling';
import whenIsTheBestTimeToSellGold from './posts/when-is-the-best-time-to-sell-gold';
import sellingJewelryInternationally from './posts/selling-jewelry-internationally';
import buyingJewelryAtFleaMarketsAndEstateSales from './posts/buying-jewelry-at-flea-markets-and-estate-sales';
import howToTrainYourEyeForAntiqueJewelry from './posts/how-to-train-your-eye-for-antique-jewelry';
import doYouNeedAGemCertificateToSell from './posts/do-you-need-a-gem-certificate-to-sell';
import loupesScalesAndToolsForCollectors from './posts/loupes-scales-and-tools-for-collectors';
import howToSellAnEntireJewelryCollection from './posts/how-to-sell-an-entire-jewelry-collection';
import howToSpotAScamBuyer from './posts/how-to-spot-a-scam-buyer';
import questionsToAskBeforeBuyingJewelryOnline from './posts/questions-to-ask-before-buying-jewelry-online';
import whatToDoIfJewelryArrivesNotAsDescribed from './posts/what-to-do-if-jewelry-arrives-not-as-described';
import isCostumeJewelryWorthAnything from './posts/is-costume-jewelry-worth-anything';
import howToSellAPocketWatch from './posts/how-to-sell-a-pocket-watch';
import whatToDoWithAnEngagementRingAfterABreakup from './posts/what-to-do-with-an-engagement-ring-after-a-breakup';
import doYouPayTaxWhenSellingJewelry from './posts/do-you-pay-tax-when-selling-jewelry';
import jewelryConsignmentExplained from './posts/jewelry-consignment-explained';
import howJewelryAuctionsWorkForSellers from './posts/how-jewelry-auctions-work-for-sellers';
import howToCatalogueYourJewelryCollection from './posts/how-to-catalogue-your-jewelry-collection';
import howToBuyJewelryAsAGift from './posts/how-to-buy-jewelry-as-a-gift';
import redFlagsInJewelryListings from './posts/red-flags-in-jewelry-listings';
import howToBuildAReputationAsASeller from './posts/how-to-build-a-reputation-as-a-seller';
import meetingABuyerInPersonSafely from './posts/meeting-a-buyer-in-person-safely';
import whyIsMyJewelryNotSelling from './posts/why-is-my-jewelry-not-selling';
import whatIsMyDiamondRingWorth from './posts/what-is-my-diamond-ring-worth';
import howToSellInheritedJewelry from './posts/how-to-sell-inherited-jewelry';
import pawnShopVsSellingOnline from './posts/pawn-shop-vs-selling-online';
import howToSpotALabGrownDiamond from './posts/how-to-spot-a-lab-grown-diamond';
import howToSellALuxuryWatch from './posts/how-to-sell-a-luxury-watch';
import howToSellJewelryWithoutHallmarks from './posts/how-to-sell-jewelry-without-hallmarks';
import whatHappensIfABuyerDoesNotPay from './posts/what-happens-if-a-buyer-does-not-pay';
import howToFilmJewelryForListings from './posts/how-to-film-jewelry-for-listings';
import howReturnsWorkWhenBuyingJewelryOnline from './posts/how-returns-work-when-buying-jewelry-online';
import howToBuyJewelryOnSocialMediaSafely from './posts/how-to-buy-jewelry-on-social-media-safely';
import whatDoesEstateJewelryMean from './posts/what-does-estate-jewelry-mean';
import howToNegotiateWhenBuyingJewelry from './posts/how-to-negotiate-when-buying-jewelry';
import isItWorthSellingBrokenGoldJewelry from './posts/is-it-worth-selling-broken-gold-jewelry';
import whenDoJewelryBuyersActuallyBuy from './posts/when-do-jewelry-buyers-actually-buy';
import valuingJewelryForProbate from './posts/valuing-jewelry-for-probate';
import howToBuildAJewelryCollection from './posts/how-to-build-a-jewelry-collection';
import getNextmineOnYourHomeScreen from './posts/get-nextmine-on-your-home-screen';

const bodies = {
  'get-nextmine-on-your-home-screen': getNextmineOnYourHomeScreen,
  'how-to-build-a-jewelry-collection': howToBuildAJewelryCollection,
  'how-to-spot-a-scam-buyer': howToSpotAScamBuyer,
  'questions-to-ask-before-buying-jewelry-online': questionsToAskBeforeBuyingJewelryOnline,
  'what-to-do-if-jewelry-arrives-not-as-described': whatToDoIfJewelryArrivesNotAsDescribed,
  'is-costume-jewelry-worth-anything': isCostumeJewelryWorthAnything,
  'how-to-sell-a-pocket-watch': howToSellAPocketWatch,
  'what-to-do-with-an-engagement-ring-after-a-breakup': whatToDoWithAnEngagementRingAfterABreakup,
  'do-you-pay-tax-when-selling-jewelry': doYouPayTaxWhenSellingJewelry,
  'jewelry-consignment-explained': jewelryConsignmentExplained,
  'how-jewelry-auctions-work-for-sellers': howJewelryAuctionsWorkForSellers,
  'how-to-catalogue-your-jewelry-collection': howToCatalogueYourJewelryCollection,
  'how-to-buy-jewelry-as-a-gift': howToBuyJewelryAsAGift,
  'red-flags-in-jewelry-listings': redFlagsInJewelryListings,
  'how-to-build-a-reputation-as-a-seller': howToBuildAReputationAsASeller,
  'meeting-a-buyer-in-person-safely': meetingABuyerInPersonSafely,
  'why-is-my-jewelry-not-selling': whyIsMyJewelryNotSelling,
  'how-to-price-used-jewelry': howToPriceUsedJewelry,
  'how-to-pack-and-ship-jewelry': howToPackAndShipJewelry,
  'how-to-describe-jewelry-condition': howToDescribeJewelryCondition,
  'how-to-document-jewelry-provenance': howToDocumentJewelryProvenance,
  'jewelry-appraisals-explained': jewelryAppraisalsExplained,
  'what-is-escrow-and-why-it-protects-both': whatIsEscrowAndWhyItProtectsBoth,
  'how-to-handle-offers-and-negotiation': howToHandleOffersAndNegotiation,
  'should-you-restore-jewelry-before-selling': shouldYouRestoreJewelryBeforeSelling,
  'when-is-the-best-time-to-sell-gold': whenIsTheBestTimeToSellGold,
  'selling-jewelry-internationally': sellingJewelryInternationally,
  'buying-jewelry-at-flea-markets-and-estate-sales': buyingJewelryAtFleaMarketsAndEstateSales,
  'how-to-train-your-eye-for-antique-jewelry': howToTrainYourEyeForAntiqueJewelry,
  'do-you-need-a-gem-certificate-to-sell': doYouNeedAGemCertificateToSell,
  'loupes-scales-and-tools-for-collectors': loupesScalesAndToolsForCollectors,
  'how-to-sell-an-entire-jewelry-collection': howToSellAnEntireJewelryCollection,
  'how-to-tell-if-gold-jewelry-is-real': howToTellIfGoldJewelryIsReal,
  'old-mine-cut-vs-old-european-cut-diamonds': oldMineCutVsOldEuropeanCutDiamonds,
  'how-to-date-antique-jewelry-by-era': howToDateAntiqueJewelryByEra,
  'how-to-sell-antique-jewelry-online': howToSellAntiqueJewelryOnline,
  'how-to-photograph-jewelry-with-your-phone': howToPhotographJewelryWithYourPhone,
  'art-deco-vs-art-nouveau-jewelry': artDecoVsArtNouveauJewelry,
  'how-to-measure-your-ring-size-at-home': howToMeasureYourRingSizeAtHome,
  'silver-hallmarks-explained': silverHallmarksExplained,
  'how-to-tell-if-a-gemstone-is-real': howToTellIfAGemstoneIsReal,
  'where-to-sell-pre-loved-fine-jewelry-online': whereToSellPreLovedFineJewelryOnline,
  'how-much-is-my-jewelry-worth': howMuchIsMyJewelryWorth,
  'how-to-sell-jewelry-safely-online': howToSellJewelrySafelyOnline,
  'how-to-clean-antique-jewelry': howToCleanAntiqueJewelry,
  'real-vs-fake-pearls': realVsFakePearls,
  'necklace-lengths-and-chain-types': necklaceLengthsAndChainTypes,
  'how-to-buy-an-antique-engagement-ring': howToBuyAnAntiqueEngagementRing,
  'how-to-identify-and-date-a-cameo': howToIdentifyAndDateACameo,
  'how-to-spot-fake-antique-jewelry': howToSpotFakeAntiqueJewelry,
  'handmade-vs-cast-jewelry': handmadeVsCastJewelry,
  'ring-settings-explained': ringSettingsExplained,
  'antique-earring-fittings-and-dating': antiqueEarringFittingsAndDating,
  'enamel-jewelry-explained': enamelJewelryExplained,
  'antique-cufflinks-and-mens-jewelry': antiqueCufflinksAndMensJewelry,
  'how-to-write-a-jewelry-listing-that-sells': howToWriteAJewelryListingThatSells,
  'customs-and-import-duties-on-jewelry': customsAndImportDutiesOnJewelry,
  'how-to-insure-antique-jewelry': howToInsureAntiqueJewelry,
  'how-to-start-collecting-antique-jewelry': howToStartCollectingAntiqueJewelry,
  'what-makes-antique-jewelry-rare': whatMakesAntiqueJewelryRare,
  'what-is-my-diamond-ring-worth': whatIsMyDiamondRingWorth,
  'how-to-sell-inherited-jewelry': howToSellInheritedJewelry,
  'pawn-shop-vs-selling-online': pawnShopVsSellingOnline,
  'how-to-spot-a-lab-grown-diamond': howToSpotALabGrownDiamond,
  'how-to-sell-a-luxury-watch': howToSellALuxuryWatch,
  'how-to-sell-jewelry-without-hallmarks': howToSellJewelryWithoutHallmarks,
  'what-happens-if-a-buyer-does-not-pay': whatHappensIfABuyerDoesNotPay,
  'how-to-film-jewelry-for-listings': howToFilmJewelryForListings,
  'how-returns-work-when-buying-jewelry-online': howReturnsWorkWhenBuyingJewelryOnline,
  'how-to-buy-jewelry-on-social-media-safely': howToBuyJewelryOnSocialMediaSafely,
  'what-does-estate-jewelry-mean': whatDoesEstateJewelryMean,
  'how-to-negotiate-when-buying-jewelry': howToNegotiateWhenBuyingJewelry,
  'is-it-worth-selling-broken-gold-jewelry': isItWorthSellingBrokenGoldJewelry,
  'when-do-jewelry-buyers-actually-buy': whenDoJewelryBuyersActuallyBuy,
  'valuing-jewelry-for-probate': valuingJewelryForProbate,
};

// Newest first. Ties keep the order they have in posts.json.
const byNewest = (a, b) => (a.published < b.published ? 1 : a.published > b.published ? -1 : 0);

/**
 * All posts, newest first, with their markdown body attached.
 *
 * @returns {Array<Object>} posts
 */
export const getAllPosts = () =>
  postsMeta
    .filter(post => !!bodies[post.slug])
    .map(post => ({ ...post, body: bodies[post.slug] }))
    .sort(byNewest);

/**
 * A single post by slug, or null when the slug is unknown.
 *
 * @param {string} slug
 * @returns {Object|null} post
 */
export const getPostBySlug = slug => {
  const post = postsMeta.find(p => p.slug === slug);
  return post && bodies[slug] ? { ...post, body: bodies[slug] } : null;
};

/**
 * The posts listed as related to the given post, in the order they are listed.
 *
 * @param {Object} post
 * @returns {Array<Object>} related posts (without bodies)
 */
export const getRelatedPosts = post =>
  (post?.related || []).map(slug => postsMeta.find(p => p.slug === slug)).filter(Boolean);

/**
 * Human readable publication date, e.g. "28 July 2026".
 *
 * @param {string} isoDate YYYY-MM-DD
 * @returns {string} formatted date
 */
export const formatPostDate = isoDate => {
  const [year, month, day] = (isoDate || '').split('-');
  // prettier-ignore
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const monthName = months[Number(month) - 1];
  return monthName ? `${Number(day)} ${monthName} ${year}` : isoDate;
};
